// runtime-config.loader.ts — startup-time loader for /assets/runtime-config.json.
//
// The one documented exception to HTTP centralization through api-client.ts
// (security baseline, repo-root CLAUDE.md). The loader runs before the
// api-client is wired because baseUrl / authBaseUrl must be known before any
// API call; the single `fetch` usage below carries an inline disable of
// no-restricted-globals, and this comment is the justification.

import { create } from 'zustand';

import { LogLevel, RuntimeConfig } from './runtime-config';

interface RuntimeConfigState {
  config: RuntimeConfig | null;
  isLoaded: boolean;
  setConfig: (c: RuntimeConfig) => void;
}

export const useRuntimeConfigStore = create<RuntimeConfigState>((set) => ({
  config: null,
  isLoaded: false,
  setConfig: (c) => set({ config: c, isLoaded: true }),
}));

export const runtimeConfigStore = useRuntimeConfigStore;

export const selectBaseUrl = (s: RuntimeConfigState): string | null =>
  s.config?.baseUrl ?? null;

export const selectAuthBaseUrl = (s: RuntimeConfigState): string | null =>
  s.config?.authBaseUrl ?? null;

const LOG_LEVEL_VALUES = new Set<number>([
  LogLevel.DEBUG,
  LogLevel.INFO,
  LogLevel.WARN,
  LogLevel.ERROR,
]);

function assertRuntimeConfig(value: unknown): asserts value is RuntimeConfig {
  if (value === null || typeof value !== 'object') {
    throw new Error('Runtime config must be a JSON object');
  }

  const v = value as Record<string, unknown>;

  // baseUrl must be non-empty because it encodes the API version prefix
  // (e.g. "/api/v1") — an empty baseUrl would silently collapse API calls
  // to the origin root and mask routing bugs.
  if (typeof v['baseUrl'] !== 'string' || v['baseUrl'].length === 0) {
    throw new TypeError('Runtime config field "baseUrl" must be a non-empty string');
  }

  // authBaseUrl permits the empty string, meaning "same-origin" — consumers
  // build URLs like `${authBaseUrl}/auth/me`, so "" produces "/auth/me" which
  // the browser resolves against the current origin. The production container
  // relies on this to keep all traffic same-origin under CSP `connect-src 'self'`.
  if (typeof v['authBaseUrl'] !== 'string') {
    throw new TypeError('Runtime config field "authBaseUrl" must be a string');
  }

  const booleanFields: Array<keyof RuntimeConfig> = [
    'production',
    'enableLogging',
    'enableCaptureTypeSelector',
  ];
  for (const field of booleanFields) {
    if (typeof v[field] !== 'boolean') {
      throw new TypeError(`Runtime config field "${field}" must be a boolean`);
    }
  }

  if (typeof v['logLevel'] !== 'number' || !LOG_LEVEL_VALUES.has(v['logLevel'])) {
    throw new TypeError('Runtime config field "logLevel" must be a LogLevel value (0-3)');
  }

  if (v['mapboxAccessToken'] !== undefined && typeof v['mapboxAccessToken'] !== 'string') {
    throw new TypeError('Runtime config field "mapboxAccessToken" must be a string');
  }
}

export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  // Sanctioned fetch usage: startup-time load of /assets/runtime-config.json
  // must run before the API client is wired, so it cannot route through
  // src/core/api/api-client.ts.
  // eslint-disable-next-line no-restricted-globals
  const response = await fetch('/assets/runtime-config.json', { cache: 'no-store' });

  if (!response.ok) {
    throw new Error(
      `Failed to load runtime config: ${response.status} ${response.statusText}`,
    );
  }

  const parsed: unknown = await response.json();
  assertRuntimeConfig(parsed);

  useRuntimeConfigStore.getState().setConfig(parsed);
  return parsed;
}
